const normalizeText = (value) => String(value || '').trim().toLowerCase();

const normalizeVariant = (value) => normalizeText(value)
  .replace(/iscc/g, '')
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

const collectScopeTextParts = (scope) => {
  if (!scope) return [];
  const parts = [
    scope.serviceName,
    scope.standardName,
    scope.serviceItemName,
    scope.label,
  ];

  if (Array.isArray(scope.memberScopes)) {
    scope.memberScopes.forEach((member) => {
      parts.push(
        member?.serviceName,
        member?.standardName,
        member?.serviceItemName,
        member?.label,
      );
    });
  }

  return parts.filter(Boolean);
};

export const isSustainabilityAuditScope = (scope) => {
  const haystack = normalizeText(collectScopeTextParts(scope).join(' '));
  if (!haystack) return false;
  return haystack.includes('sustainability') || haystack.includes('iscc');
};

// ISCC has a shared core catalog plus variant-specific evidence/forms. Keep the
// matching here so the visible fields and completion gate use the same rules.
export const getIsccStandardVariant = (scope) => {
  const scopeText = normalizeText(collectScopeTextParts(scope).join(' '));
  if (scopeText.includes('corsia') && scopeText.includes('plus')) return 'corsia_plus';
  if (scopeText.includes('corsia')) return 'corsia';
  if (scopeText.includes('japan') && scopeText.includes('fit')) return 'japan_fit';
  if (scopeText.includes('plus')) return 'plus';
  if (scopeText.includes('eu')) return 'eu';
  return null;
};

const getCatalogVariantKeys = (item) => {
  const explicit = [item?.variant, item?.variantMask]
    .flatMap((value) => String(value || '').split(/[,;|]/))
    .map(normalizeVariant)
    .filter(Boolean);
  const codeText = normalizeText(item?.documentCode || item?.documentName || '');
  // Older rows were seeded as ALL even when their code was standard-specific.
  const codeVariants = [];
  if (codeText.includes('corsia-plus') || codeText.includes('corsia_plus')) codeVariants.push('corsia_plus');
  else if (codeText.includes('corsia')) codeVariants.push('corsia');
  if (codeText.includes('japan-fit') || codeText.includes('japan_fit')) codeVariants.push('japan_fit');
  return Array.from(new Set(codeVariants.length > 0
    ? [...explicit.filter((key) => key !== 'all'), ...codeVariants]
    : explicit));
};

export const isCatalogItemApplicableToScope = (item, scope) => {
  const itemVariants = getCatalogVariantKeys(item);
  if (itemVariants.length === 0 || itemVariants.includes('all')) return true;
  const scopeVariant = getIsccStandardVariant(scope);
  return Boolean(scopeVariant && itemVariants.includes(scopeVariant));
};

const normalizeCatalogStatus = (item) => normalizeText(item?.status || item?.documentStatus || 'active');

const isActiveCatalogItem = (item) => {
  const status = normalizeCatalogStatus(item);
  return !['inactive', 'disabled', 'archived', 'deleted'].includes(status);
};

export const mapCatalogItemsToUploadDocs = (items = [], selectedScope = null) => {
  const seen = new Set();
  return (Array.isArray(items) ? items : [])
    .filter((item) => item?.documentCode && isActiveCatalogItem(item) && isCatalogItemApplicableToScope(item, selectedScope))
    .map((item) => ({
      code: String(item.documentCode).trim(),
      title: String(item.documentName || item.name || '').trim(),
      inputMode: item.inputMode || 'upload_only',
      catalogGroup: item.catalogGroup || '',
      requiredRole: item.requiredRole || '',
      version: item.version || '',
      uploadOnly: true,
      catalogItem: item,
    }))
    .filter((doc) => {
      const key = normalizeText(doc.code);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

// ISCC uses catalog-backed documents rather than the legacy FAPP form keys.
// Required certificate-review artifacts belong to the later certificate stage
// and must not prevent the Audit task from completing.
export const isIsccAuditCatalogComplete = ({ catalogItems = [], uploadedDocuments = [], scope = null }) => {
  const uploadedCodes = new Set((Array.isArray(uploadedDocuments) ? uploadedDocuments : [])
    .map((document) => String(document?.documentCode || document?.description || '').trim())
    .filter(Boolean));

  const requiredAuditItems = (Array.isArray(catalogItems) ? catalogItems : [])
    .filter((item) => item?.documentCode && isActiveCatalogItem(item))
    // The catalog API exposes this flag as `uploadRequired`; retain the
    // `required`/`mandatory` aliases for older catalog payloads.
    .filter((item) => item?.uploadRequired === true || item?.required === true || item?.mandatory === true)
    .filter((item) => !['certificate_reviewer', 'certification_authority', 'certificate_issuer']
      .includes(normalizeText(item?.requiredRole)))
    .filter((item) => isCatalogItemApplicableToScope(item, scope));

  return requiredAuditItems.length > 0
    && requiredAuditItems.every((item) => uploadedCodes.has(String(item.documentCode).trim()));
};

// FAPP-06 (Certificate Information Form + its signed-by-client CIF) and FAPP-08
// (Change Notice Form) only apply once a certificate can actually be issued —
// Stage 2, never Stage 1. Filtered out of the resolved doc list entirely
// (not just disabled) so Stage 1's Audit tab neither shows them nor counts
// them toward "all forms saved" for that scope.
export const STAGE2_ONLY_AUDIT_DOC_CODES = new Set([
  'FAPP-06 Certificate Information Form',
  'FAPP-06 CIF (signed by client)',
  'FAPP-08 Change Notice Form',
]);

export const resolveAuditDocumentsForScope = ({
  activeDocTab,
  baseDocs = [],
  catalogItems = [],
  selectedScope = null,
  isStage1 = false,
}) => {
  const base = (Array.isArray(baseDocs) ? baseDocs : []).map((code) => ({ code, uploadOnly: false }));
  if (activeDocTab !== 'audit' || !isSustainabilityAuditScope(selectedScope)) {
    if (activeDocTab === 'audit' && isStage1) {
      return base.filter((doc) => !STAGE2_ONLY_AUDIT_DOC_CODES.has(doc.code));
    }
    return base;
  }

  return mapCatalogItemsToUploadDocs(catalogItems, selectedScope);
};
